import { Box, Button, Heading, Text } from '@chakra-ui/react'
import { useDispatch } from 'react-redux'

import InputGroupContent from '../elements/input'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { loginCodeSchema, loginSchema } from '@/entities/schemas/login'
import { EmailAndPasswordInterface, CodeInterface } from '@/entities/app/auth/auth'
import Toast from '../elements/toast'
import {
	authWithEmailAndPassword,
	verifyUserWithCode,
} from '@/services/api/auth/auth'
import decodeJWT from '@/utils/decode-jwt'
import { AppDispatch } from '@/store/store'
import { login } from '@/store/features/auth'
import { saveSession } from '@/services/session/session'
import { useNavigate } from 'react-router-dom'
import JwtPayload from '@/entities/api/jwt'
import SpinnerLoading from '../elements/spinner'
import { useState } from 'react'

export const EmailAndPassword = ({
	state,
	emailState,
}: {
	state: (value: string) => void
	emailState: (value: string) => void
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<EmailAndPasswordInterface>({
		resolver: yupResolver(loginSchema),
	})
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const onSubmit = async (data: EmailAndPasswordInterface) => {
		try {
			setIsLoading(true)
			emailState(data.email)

			const response = await authWithEmailAndPassword({
				email: data.email,
				password: data.password,
			})

			if (response?.status === 401) {
				Toast({ type: 'error', message: 'La contraseña es incorrecta' })
				setIsLoading(false)
				return
			}

			if (response?.status === 404) {
				Toast({ type: 'error', message: 'El usuario no existe' })
				setIsLoading(false)
				return
			}

			if (response?.status === 404) {
				Toast({ type: 'error', message: 'El usuario no existe' })
				setIsLoading(false)
				return
			}

			if (response?.status === 500) {
				Toast({ type: 'error', message: 'Error interno del servidor' })
				setIsLoading(false)
				return
			}

			setIsLoading(false)
			Toast({
				type: 'success',
				message: 'Se envio un correo con el codigo de verificacion',
			})
			state('code')
		} catch (error) {
			setIsLoading(false)
			Toast({ type: 'error', message: 'Algo salio mal a iniciar session' })
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className='w-full'>
			<Heading
				as='h2'
				fontSize={{ base: '2xl', lg: '3xl', xl: '4xl' }}
				fontWeight='900'
				color='black'
				mb='2'
				textAlign='center'
			>
				Inicia sesion
			</Heading>
			<AlertIp />
			<Box w='full'>
				<InputGroupContent
					label='Correo'
					placeholder='Ingresa tu correo'
					type='email'
					name='email'
					register={register}
					errors={errors}
				/>
			</Box>
			<Box w='full' mt='2'>
				<InputGroupContent
					label='Contraseña'
					placeholder='Ingresa tu contraseña'
					type='password'
					name='password'
					register={register}
					errors={errors}
				/>
			</Box>
			<Button
				p={{ base: '4', xl: '6' }}
				fontWeight='900'
				mt='4'
				fontSize={{ base: 'lg', xl: 'xl' }}
				bg='black'
				color='white'
				type='submit'
			>
				{isLoading ? <SpinnerLoading color='white' /> : 'Iniciar sesion'}
			</Button>
		</form>
	)
}

export const SendEmailCode = ({
	email,
	state,
}: {
	email: string
	state: (value: string) => void
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<CodeInterface>({
		resolver: yupResolver(loginCodeSchema),
	})
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const onSubmit = async (data: CodeInterface) => {
		try {
			setIsLoading(true)
			const response = await verifyUserWithCode(data.code)

			if (response?.status === 404 || response?.status === 401) {
				Toast({ type: 'error', message: 'El codigo es incorrecto' })
				setIsLoading(false)
				return
			}

			const token = response?.data
			const body: JwtPayload | null = decodeJWT(token)

			if (!body?.sub) {
				Toast({ type: 'error', message: 'Error al iniciar session' })
				setIsLoading(false)
				return
			}

			saveSession({ token, id: body?.sub, data: body })
			dispatch(login({ token, id: body?.sub, data: body }))
			navigate('/')

			setIsLoading(false)
		} catch (error) {
			setIsLoading(false)
			Toast({ type: 'error', message: 'Algo salio mal a iniciar session' })
		}
	}
	return (
		<form onSubmit={handleSubmit(onSubmit)} className='w-full'>
			<Heading
				as='h2'
				fontSize={{ base: '2xl', lg: '3xl', xl: '4xl' }}
				fontWeight='900'
				color='black'
				mb='2'
				textAlign='center'
			>
				Ingresa el codigo
			</Heading>
			<Text
				fontSize='sm'
				mb='2'
				mt='2'
				fontWeight='400'
				color='gray.600'
				textAlign='center'
				whiteSpace='normal'
				wordBreak='break-word'
			>
				{`Ingresa el código que te enviamos al correo ${email}.`}
			</Text>
			<Box w='full'>
				<InputGroupContent
					label='code'
					placeholder='Ingresa el codigo que te enviamos a tu correo'
					type='number'
					name='code'
					errors={errors}
					register={register}
				/>
			</Box>
			<Button
				p={{ base: '4', xl: '6' }}
				fontWeight='900'
				mt='4'
				fontSize={{ base: 'lg', xl: 'xl' }}
				bg='black'
				color='white'
				type='submit'
			>
				{isLoading ? <SpinnerLoading color='white' /> : 'Confirmar'}
			</Button>
			<Button
				p={{ base: '4', xl: '6' }}
				ml='4'
				fontWeight='900'
				mt='4'
				fontSize={{ base: 'lg', xl: 'xl' }}
				bg='black'
				color='white'
				onClick={() => state('email')}
			>
				Volver
			</Button>
		</form>
	)
}

const AlertIp: React.FC = () => {
	return (
		<Text
			fontSize='sm'
			mb='2'
			mt='2'
			fontWeight='400'
			color='gray.600'
			textAlign='center'
			whiteSpace='normal'
			wordBreak='break-word'
		>
			Este Ingreso esta protegido, las solicitudes se guardaran con la ip y
			datos del solicitante.
		</Text>
	)
}
